/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { useSyncExternalStore } from 'react';

/** The Agent column (`data-agent-panel`): working in it takes the keyboard from the focused window. */
const AGENT_PANEL_SELECTOR = '[data-agent-panel]';
/** Portaled overlays (dialogs, menus, popovers), which belong to whoever opened them. */
const OVERLAY_SELECTOR = '.MuiModal-root, .MuiPopover-root, .MuiPopper-root';

let inAgentPanel = false;
const listeners = new Set<() => void>();

const set = (next: boolean) => {
  if (next === inAgentPanel) return;
  inAgentPanel = next;
  for (const listener of listeners) listener();
};

/**
 * Whether an element hands the keyboard to the Agent (true) or the desktop (false). Portaled
 * overlays sit outside the Agent column but belong to whoever opened them, so they change nothing
 * (undefined): editing the Agent's share dialog keeps the windows' shortcuts off.
 */
const ownerOf = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return undefined;
  if (target.closest(AGENT_PANEL_SELECTOR)) return true;
  if (target.closest(OVERLAY_SELECTOR)) return undefined;
  return false;
};

// Pointer presses and focus moves both count: clicking a window's chrome focuses no element.
const track = (event: Event) => {
  const owner = ownerOf(event.target);
  if (owner !== undefined) set(owner);
};

const subscribe = (listener: () => void) => {
  if (listeners.size === 0) {
    // Nothing tracked while no window was open (e.g. the Agent opening the first one while its
    // composer has focus): start from wherever the keyboard is now.
    inAgentPanel = ownerOf(document.activeElement) ?? false;
    document.addEventListener('pointerdown', track, true);
    document.addEventListener('focusin', track, true);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size > 0) return;
    document.removeEventListener('pointerdown', track, true);
    document.removeEventListener('focusin', track, true);
  };
};

/** Hands the keyboard back to the focused window, e.g. once the Agent column closes. */
export const releaseAgentPanelKeyboard = () => set(false);

/** Whether the user last worked in the Agent column rather than a desktop window. */
export const useKeyboardInAgentPanel = () =>
  useSyncExternalStore(
    subscribe,
    () => inAgentPanel,
    () => false,
  );
